<template>
  <BContainer
    fluid
    class="security-violations-page pb-4"
  >
    <section class="mobile-hero mb-3">
      <div class="mobile-hero__icon">
        <i class="fas fa-shield-check" />
      </div>
      <div class="mobile-hero__text">
        <h4 class="mb-1">
          Security Violation Desk
        </h4>
        <p class="mb-0">
          Capture reports on-site and track incident handling in one screen.
        </p>
      </div>
    </section>

    <BRow class="mb-3">
      <BCol>
        <BButton
          variant="primary"
          @click="$router.push({ name: 'security.violation-report' })"
        >
          <i class="fas fa-plus me-1" />
          Report New Violation
        </BButton>
      </BCol>
    </BRow>

    <BCard class="mobile-card">
      <BCardBody class="p-3 p-md-4">
        <h6 class="section-title mb-3">
          My Submitted Reports
        </h6>

        <BRow class="g-2 align-items-end mb-2">
          <BCol
            cols="12"
            md="5"
          >
            <BFormGroup
              label="Search"
              label-class="form-label-mobile"
              class="mb-2"
            >
              <BFormInput
                v-model="filters.filter_text"
                placeholder="Search student, type, location"
                @keyup.enter="fetchRows(1)"
              />
            </BFormGroup>
          </BCol>
          <BCol
            cols="6"
            md="3"
          >
            <BFormGroup
              label="Status"
              label-class="form-label-mobile"
              class="mb-2"
            >
              <VSelect
                v-model="selected.status"
                :options="statusOptions"
                :reduce="v => v.id"
                label="name"
                placeholder="All"
              />
            </BFormGroup>
          </BCol>
          <BCol
            cols="6"
            md="3"
          >
            <BFormGroup
              label="Severity"
              label-class="form-label-mobile"
              class="mb-2"
            >
              <VSelect
                v-model="selected.severity"
                :options="severityOptions"
                :reduce="v => v.id"
                label="name"
                placeholder="All"
              />
            </BFormGroup>
          </BCol>
          <BCol
            cols="12"
            md="1"
          >
            <BButton
              size="sm"
              variant="outline-secondary"
              class="w-100"
              @click.prevent="resetFilters"
            >
              Reset
            </BButton>
          </BCol>
        </BRow>

        <BOverlay
          :show="state.loading"
          opacity="0.35"
        >
          <div class="d-md-none">
            <div
              v-if="rows.length === 0"
              class="empty-state"
            >
              No violation reports found.
            </div>
            <div
              v-for="item in rows"
              :key="item.id"
              class="report-mobile-item"
            >
              <div class="report-mobile-item__head">
                <strong>{{ formatStudent(item.student) }}</strong>
                <BBadge :variant="statusVariant(item.status)">
                  {{ item.status }}
                </BBadge>
              </div>
              <div class="small text-muted">
                {{ getViolationName(item) }} • {{ getSeverityLabel(item) }}
              </div>
              <div class="small mt-1">
                {{ item.location || 'No location' }}
              </div>
              <div class="small text-muted mt-1">
                {{ item.occurred_at ? dateTimeShortFormatter(item.occurred_at) : 'N/A' }}
              </div>
              <div class="small mt-2">
                {{ item.description || 'No description' }}
              </div>

              <div class="d-flex gap-2 mt-2">
                <BButton
                  size="sm"
                  variant="outline-secondary"
                  @click.prevent="openReportDetails(item)"
                >
                  Details
                </BButton>
                <BButton
                  v-if="item.evidence_url"
                  size="sm"
                  variant="outline-primary"
                  :disabled="state.loadingEvidence"
                  @click.prevent="openEvidencePreview(item)"
                >
                  Evidence
                </BButton>
              </div>
            </div>
          </div>

          <BTable
            class="d-none d-md-table mt-2"
            :items="rows"
            :fields="fields"
            responsive
            show-empty
            small
          >
            <template #empty>
              <div class="text-center py-3 text-muted">
                No violation reports found.
              </div>
            </template>
            <template #cell(student)="{ item }">
              {{ formatStudent(item.student) }}
            </template>
            <template #cell(violation_type)="{ item }">
              {{ getViolationName(item) }}
            </template>
            <template #cell(severity)="{ item }">
              <BBadge :variant="severityVariant(getSeverity(item))">
                {{ getSeverityLabel(item) }}
              </BBadge>
            </template>
            <template #cell(status)="{ value }">
              <BBadge :variant="statusVariant(value)">
                {{ value }}
              </BBadge>
            </template>
            <template #cell(occurred_at)="{ value }">
              {{ value ? dateTimeShortFormatter(value) : 'N/A' }}
            </template>

            <template #cell(evidence)="{ item }">
              <BButton
                v-if="item.evidence_url"
                size="sm"
                variant="outline-primary"
                :disabled="state.loadingEvidence"
                @click.prevent="openEvidencePreview(item)"
              >
                View
              </BButton>
              <span v-else>—</span>
            </template>
          </BTable>
        </BOverlay>

        <div
          v-if="pagination.last_page > 1"
          class="d-flex justify-content-end mt-3"
        >
          <BPagination
            v-model="pagination.current_page"
            :total-rows="pagination.total"
            :per-page="pagination.per_page"
            size="sm"
            @change="fetchRows"
          />
        </div>
      </BCardBody>
    </BCard>

    <BModal
      ref="modalReportDetails"
      title="Violation Details"
      centered
      hide-footer
    >
      <div v-if="selectedReport">
        <p class="mb-2">
          <strong>Student:</strong> {{ formatStudent(selectedReport.student) }}
        </p>
        <p class="mb-2">
          <strong>Type:</strong> {{ getViolationName(selectedReport) }}
        </p>
        <p class="mb-2">
          <strong>Severity:</strong> {{ getSeverityLabel(selectedReport) }}
        </p>
        <p class="mb-2">
          <strong>Status:</strong> {{ selectedReport.status || 'N/A' }}
        </p>
        <p class="mb-2">
          <strong>Date:</strong> {{ selectedReport.occurred_at ? dateTimeShortFormatter(selectedReport.occurred_at) : 'N/A' }}
        </p>
        <p class="mb-2">
          <strong>Location:</strong> {{ selectedReport.location || 'N/A' }}
        </p>
        <p class="mb-2">
          <strong>Description:</strong> {{ selectedReport.description || 'No description' }}
        </p>
        <BButton
          v-if="selectedReport.evidence_url"
          size="sm"
          variant="outline-primary"
          :disabled="state.loadingEvidence"
          @click.prevent="openEvidencePreview(selectedReport)"
        >
          View Evidence
        </BButton>
      </div>
    </BModal>

    <BModal
      ref="modalEvidencePreview"
      title="Violation Evidence"
      size="lg"
      centered
      hide-footer
    >
      <div
        v-if="state.loadingEvidence"
        class="text-center py-4"
      >
        <i class="fas fa-spinner fa-spin fa-2x text-primary" />
      </div>

      <div
        v-else-if="evidencePreviewUrl"
        class="text-center"
      >
        <img
          :src="evidencePreviewUrl"
          alt="Violation evidence"
          class="img-fluid rounded border"
        >
      </div>

      <div
        v-else
        class="text-center text-muted py-4"
      >
        No evidence image available.
      </div>
    </BModal>
  </BContainer>
</template>

<script>
import MxFormatter from '@/mixins/formatter'
import { SSharedViolation } from '@/services/shared'

export default {
  name: 'SecurityViolations',

  mixins: [MxFormatter],

  middleware: ['auth', 'security-personel'],

  metaInfo: () => ({ title: 'Security Violation Reports' }),

  data () {
    return {
      state: {
        loading: false,
        loadingEvidence: false
      },
      rows: [],
      filters: {
        filter_text: '',
        status: '',
        severity: ''
      },
      selected: {
        status: null,
        severity: null
      },
      evidencePreviewUrl: '',
      selectedReport: null,
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
      }
    }
  },

  computed: {
    fields () {
      return [
        { key: 'student', label: 'Student' },
        { key: 'violation_type', label: 'Violation Type' },
        { key: 'severity', label: 'Severity' },
        { key: 'description', label: 'Description' },
        { key: 'location', label: 'Location' },
        { key: 'evidence', label: 'Evidence' },
        { key: 'occurred_at', label: 'Occurred At' },
        { key: 'status', label: 'Status' }
      ]
    },
    statusOptions () {
      return [
        { id: '', name: 'All' },
        { id: 'pending', name: 'Pending' },
        { id: 'resolved', name: 'Resolved' },
        { id: 'dismissed', name: 'Dismissed' }
      ]
    },
    severityOptions () {
      return [
        { id: '', name: 'All' },
        { id: 'warning', name: 'Warning' },
        { id: 'minor', name: 'Minor' },
        { id: 'moderate', name: 'Moderate' },
        { id: 'major', name: 'Major' },
        { id: 'severe', name: 'Severe' }
      ]
    }
  },

  watch: {
    'selected.status' (value) {
      this.filters.status = value || ''
      this.fetchRows(1)
    },
    'selected.severity' (value) {
      this.filters.severity = value || ''
      this.fetchRows(1)
    }
  },

  mounted () {
    this.fetchRows(1)
  },

  beforeDestroy () {
    if (this.evidencePreviewUrl) {
      URL.revokeObjectURL(this.evidencePreviewUrl)
    }
  },

  methods: {
    statusVariant (status) {
      const map = { pending: 'warning', resolved: 'success', dismissed: 'secondary' }
      return map[status] || 'secondary'
    },

    severityVariant (severity) {
      const map = { warning: 'info', minor: 'primary', moderate: 'warning', major: 'danger', severe: 'dark' }
      return map[severity] || 'secondary'
    },

    formatStudent (student) {
      if (!student) return 'N/A'
      const lastName = student.last_name || ''
      const firstName = student.first_name || ''
      const combined = `${lastName}${lastName && firstName ? ', ' : ''}${firstName}`.trim()
      return combined || 'N/A'
    },

    getViolationName (item) {
      return item && item.violation_type && item.violation_type.name ? item.violation_type.name : 'N/A'
    },

    getSeverity (item) {
      return item && item.violation_type ? item.violation_type.severity_level : ''
    },

    getSeverityLabel (item) {
      return this.getSeverity(item) || 'N/A'
    },

    getEvidenceFilename (evidenceUrl) {
      if (!evidenceUrl || typeof evidenceUrl !== 'string') return ''
      const parts = evidenceUrl.split('/')
      return parts.length ? parts[parts.length - 1] : ''
    },

    async openEvidencePreview (violation) {
      const filename = this.getEvidenceFilename(violation?.evidence_url)
      if (!filename) {
        if (this.evidencePreviewUrl) {
          URL.revokeObjectURL(this.evidencePreviewUrl)
        }
        this.evidencePreviewUrl = ''
        this.$refs.modalEvidencePreview.show()
        return
      }

      this.state.loadingEvidence = true
      if (this.evidencePreviewUrl) {
        URL.revokeObjectURL(this.evidencePreviewUrl)
      }
      this.evidencePreviewUrl = ''
      this.$refs.modalEvidencePreview.show()

      try {
        const { data } = await SSharedViolation.getEvidence(filename)
        this.evidencePreviewUrl = URL.createObjectURL(data)
      } catch {
        this.evidencePreviewUrl = ''
      } finally {
        this.state.loadingEvidence = false
      }
    },

    openReportDetails (item) {
      this.selectedReport = item
      this.$refs.modalReportDetails.show()
    },

    async fetchRows (page = 1) {
      this.state.loading = true
      this.pagination.current_page = page

      try {
        const payload = {
          page,
          per_page: this.pagination.per_page,
          sort: 'occurred_at',
          sort_desc: true,
          filter_text: this.filters.filter_text,
          status: this.filters.status,
          severity: this.filters.severity
        }

        const { data } = await SSharedViolation.myViolations(payload)
        const items = data.items || data.data || []
        this.rows = items
        this.pagination.total = data.total_rows || data.total || items.length
        this.pagination.per_page = data.per_page || data.pagination?.per_page || this.pagination.per_page
        this.pagination.current_page = data.current_page || data.pagination?.current_page || this.pagination.current_page
        this.pagination.last_page = data.last_page || data.pagination?.last_page || Math.max(1, Math.ceil(this.pagination.total / this.pagination.per_page))
      } catch {
        this.rows = []
      } finally {
        this.state.loading = false
      }
    },

    resetFilters () {
      this.filters.filter_text = ''
      this.filters.status = ''
      this.filters.severity = ''
      this.selected.status = null
      this.selected.severity = null
      this.fetchRows(1)
    }
  }
}
</script>

<style scoped>
.security-violations-page {
  --osas-green-700: #0b7a3d;
  --osas-green-600: #149647;
  --osas-gold-500: #f2b705;
  --osas-gold-100: #fff4cc;
  --osas-surface: #ffffff;
  --osas-border: #e9ecef;
  --osas-text: #1f2937;
  --osas-muted: #6b7280;
  background: linear-gradient(180deg, #f7fbf5 0%, #fdfdf9 100%);
}

.mobile-hero {
  background: linear-gradient(135deg, var(--osas-green-700), var(--osas-green-600));
  border-radius: 1rem;
  padding: 1rem;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  color: #fff;
  box-shadow: 0 14px 30px rgba(20, 150, 71, 0.25);
}

.mobile-hero__icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-hero__text h4 {
  color: #fff;
  font-size: 1.05rem;
  font-weight: 700;
}

.mobile-hero__text p {
  color: #fff;
  font-size: 0.8rem;
  opacity: 0.92;
}

/* Mobile-first normal filter control sizing */
.security-violations-page .form-control {
  min-height: 34px;
  height: 34px;
  padding: 0.35rem 0.6rem;
  font-size: 0.84rem;
}

.security-violations-page .vs__dropdown-toggle {
  min-height: 34px;
  height: 34px;
  padding: 0 0.25rem;
  font-size: 0.84rem;
}

.security-violations-page .vs__selected,
.security-violations-page .vs__search {
  font-size: 0.84rem;
  line-height: 1.2;
}

.mobile-card {
  border: 1px solid var(--osas-border);
  border-radius: 1rem;
  box-shadow: 0 8px 16px rgba(19, 23, 32, 0.06);
}

.section-title {
  color: var(--osas-text);
  font-weight: 700;
}

.form-label-mobile {
  margin-bottom: 0.35rem;
  color: var(--osas-muted);
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.preview-wrap {
  border: 1px solid var(--osas-border);
  border-radius: 0.75rem;
  padding: 0.65rem;
  background: #fff;
  max-width: 360px;
  margin-inline: auto;
}

.preview-img {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  object-position: center;
  border-radius: 0.5rem;
}

.camera-modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.25rem;
}

.camera-modal-content {
  border-radius: 1rem;
}

.camera-stage {
  position: relative;
  width: min(88vw, 360px);
  padding-top: min(88vw, 360px);
  overflow: hidden;
  border-radius: 0.8rem;
  background: #000;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.35);
}

.camera-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform-origin: center center;
}

.camera-guide {
  position: absolute;
  inset: 12%;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.28);
  pointer-events: none;
}

.camera-actions .btn {
  min-width: 104px;
}

.image-upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.45rem 0.9rem;
  border-radius: 0.65rem;
  background: #ffffff;
  color: var(--osas-green-700);
  border: 1px solid var(--osas-border);
  cursor: pointer;
  font-weight: 600;
  position: relative;
  overflow: visible;
  min-height: 38px;
}

.image-upload-btn--camera {
  border-color: var(--osas-gold-500);
  color: #6b5200;
  background: var(--osas-gold-100);
}

.image-upload-btn:hover {
  background: var(--osas-gold-100);
}

.image-upload-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 1000;
}

.mobile-submit-wrap .btn {
  font-weight: 700;
  border-radius: 0.7rem;
  background: linear-gradient(135deg, var(--osas-green-700), var(--osas-green-600));
  border: none;
}

.report-mobile-item {
  border: 1px solid var(--osas-border);
  border-left: 4px solid var(--osas-gold-500);
  background: var(--osas-surface);
  border-radius: 0.75rem;
  padding: 0.75rem;
  margin-bottom: 0.6rem;
}

.report-mobile-item__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.empty-state {
  text-align: center;
  padding: 1rem;
  border: 1px dashed var(--osas-border);
  border-radius: 0.75rem;
  color: var(--osas-muted);
  background: #fff;
}

@media (min-width: 768px) {
  .mobile-hero {
    padding: 1.25rem;
    align-items: center;
  }

  .mobile-hero__icon {
    width: 52px;
    height: 52px;
    min-width: 52px;
    font-size: 1.2rem;
  }

  .mobile-hero__text h4 {
    font-size: 1.25rem;
  }

  .mobile-hero__text p {
    font-size: 0.9rem;
  }
}
</style>
